import { BarChart3 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { type KeyUsage, useApiKeyUsage } from '@/api/hooks/use-api-key-usage'
import { StatusBadge } from '@/components/ui/status-badge'

type Period = '7d' | '30d' | '90d'

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
]

function deliveryRate(sent: number, delivered: number): string {
  if (sent === 0) return '-'
  return `${((delivered / sent) * 100).toFixed(1)}%`
}

function formatNumber(n: number): string {
  return n.toLocaleString()
}

export function ApiKeyUsageSection(): React.JSX.Element {
  const { data, isLoading } = useApiKeyUsage()
  const [period, setPeriod] = useState<Period>('30d')

  if (isLoading) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 py-12 text-center">
        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <p className="mt-3 text-sm text-text-tertiary">Loading usage data...</p>
      </div>
    )
  }

  const keys = data?.data ?? []
  const hasUsage = keys.some((k) => k.usage[period].sent > 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">Usage by API Key</h2>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      {!hasUsage ? <EmptyUsage period={period} /> : <UsageTable keys={keys} period={period} />}
    </div>
  )
}

function PeriodToggle({
  value,
  onChange,
}: {
  value: Period
  onChange: (p: Period) => void
}): React.JSX.Element {
  return (
    <div className="inline-flex rounded-lg border border-surface-3 bg-surface-1 p-0.5">
      {PERIOD_OPTIONS.map((opt) => (
        <button
          type="button"
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            value === opt.value
              ? 'bg-surface-0 text-text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function UsageTable({ keys, period }: { keys: KeyUsage[]; period: Period }): React.JSX.Element {
  const sorted = [...keys].sort((a, b) => b.usage[period].sent - a.usage[period].sent)

  return (
    <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-surface-3 bg-surface-1">
            <tr>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Key Name
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Type
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Sent
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Delivered
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Bounced
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Complained
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                Delivery Rate
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-2">
            {sorted.map((key) => (
              <UsageRow key={key.key_id} keyData={key} period={period} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UsageRow({ keyData, period }: { keyData: KeyUsage; period: Period }): React.JSX.Element {
  const navigate = useNavigate()
  const stats = keyData.usage[period]
  const typeBadgeStatus = keyData.key_type === 'live' ? 'active' : 'pending'

  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-surface-1"
      onClick={() => navigate(`/emails?api_key_id=${keyData.key_id}`)}
    >
      <td className="whitespace-nowrap px-4 py-3 font-medium text-text-primary">
        {keyData.key_name}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusBadge status={typeBadgeStatus}>{keyData.key_type}</StatusBadge>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-text-primary">
        {formatNumber(stats.sent)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-text-primary">
        {formatNumber(stats.delivered)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-text-primary">
        <span className={stats.bounced > 0 ? 'text-danger' : ''}>
          {formatNumber(stats.bounced)}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-text-primary">
        <span className={stats.complained > 0 ? 'text-danger' : ''}>
          {formatNumber(stats.complained)}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums font-medium text-text-primary">
        {deliveryRate(stats.sent, stats.delivered)}
      </td>
    </tr>
  )
}

function EmptyUsage({ period }: { period: Period }): React.JSX.Element {
  const label = PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-12 text-center">
      <BarChart3 className="mb-3 h-10 w-10 text-text-tertiary" />
      <p className="text-sm text-text-secondary">No emails sent in the last {label}</p>
    </div>
  )
}
